import { useState, useEffect } from 'react';
import { Button, Checkbox } from '@resideo/blueprint-oneportal';
import styles from './CameraAccessModal.module.css';

export interface CameraDevice {
  id: string;
  name: string | null;
}

export interface CameraAccessModalProps {
  locationName: string;
  devices: CameraDevice[];
  initialSelected: Set<string>;
  onSave?: (selected: Set<string>) => void;
  onCancel: () => void;
  readOnly?: boolean;
}

export function CameraAccessModal({
  locationName,
  devices,
  initialSelected,
  onSave,
  onCancel,
  readOnly = false,
}: CameraAccessModalProps) {
  const [localSelected, setLocalSelected] = useState<Set<string>>(
    () => new Set(initialSelected),
  );

  // Sync if modal is re-opened with different initialSelected
  useEffect(() => {
    setLocalSelected(new Set(initialSelected));
  }, [initialSelected]);

  // Close on Escape key
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const allChecked = devices.length > 0 && devices.every(d => localSelected.has(d.id));
  const someChecked = !allChecked && devices.some(d => localSelected.has(d.id));

  function toggleDevice(id: string) {
    setLocalSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleSelectAll() {
    if (allChecked) {
      setLocalSelected(new Set());
    } else {
      setLocalSelected(new Set(devices.map(d => d.id)));
    }
  }

  return (
    <div
      className={styles.overlay}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="cam-modal-title">

        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 id="cam-modal-title" className={styles.modalTitle}>Camera Access</h2>
          {readOnly ? (
            <p className={styles.modalSubtitle}>
              {locationName}
              <span className={styles.modalSubtitleSep}>|</span>
              {initialSelected.size} of {devices.length} device{devices.length !== 1 ? 's' : ''}
            </p>
          ) : (
            <p className={styles.modalSubtitle}>
              {locationName}
              <span className={styles.modalSubtitleSep}>|</span>
              {localSelected.size} of {devices.length} device{devices.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Device list */}
        <div className={styles.deviceList}>
          <div className={styles.deviceListInner}>

            {/* Select-all "Device" header row */}
            <div className={styles.deviceHeader}>
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onChange={handleSelectAll}
                disabled={readOnly}
              />
              <span className={styles.deviceHeaderLabel}>Device</span>
            </div>

            {/* Individual device rows */}
            {devices.length === 0 ? (
              <div className={styles.deviceRow}>
                <span className={styles.deviceName} style={{ color: 'var(--text-neutral-secondary, #5E646E)' }}>
                  No cameras found for this location.
                </span>
              </div>
            ) : (
              devices.map(device => (
                <div key={device.id} className={styles.deviceRow}>
                  <Checkbox
                    checked={localSelected.has(device.id)}
                    onChange={() => toggleDevice(device.id)}
                    disabled={readOnly}
                  />
                  <span className={styles.deviceName}>
                    {device.name ?? 'Camera'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          {readOnly ? (
            <Button variant="primary" className={styles.saveButton} onClick={onCancel}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="secondary" className={styles.cancelButton} onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" className={styles.saveButton} onClick={() => onSave?.(localSelected)}>
                Save
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
